import { Action } from '@/_core/fluxstore/types';
import { StorymapState } from '../../types';
import { useStorymapStore } from '../../useModule';
import { getCardById, isActivity, isSubtask, isTask } from '../../use';
import orderBy from 'lodash/orderBy';
import { isDragInProgress, onDragAbandoned } from '@/_core/app-components/dragInProgress';

const { registerReducer, dispatch } = useStorymapStore();

// A drag whose list was destroyed gets no end event; without this the board or Tracking would stay in its dragging state.
onDragAbandoned(() => {
    if (useStorymapStore().state.isDraggingCard && !isDragInProgress()) {
        dispatch(new SetIsDraggingCard(false));
    }
});

export class SetIsDraggingCard extends Action<StorymapState> {
    public static ACTION_TYPE = 'SetIsDraggingCard';

    public constructor(public isDragging: boolean, public event?: any) {
        super(SetIsDraggingCard.ACTION_TYPE);
    }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const reducer = (state: StorymapState, action: SetIsDraggingCard): void => {
    state.isDraggingCard = action.isDragging;

    if (!action.isDragging) {
        state.dragState.cardBeingDragged = null;
        state.dragState.targetParentCard = null;
        state.dragState.newIndex = null;
        state.dragState.isCardDraggedOut = false;
        // state.dragState.hasJustBeenDraggedOut will be reset in setTimeout
        return;
    }

    if (!action.event) {
        return;
    }

    const e = action.event;

    const relatedForIndex = e.relatedContext?.element;
    const isStoryOverStoryCell =
        isSubtask(e.draggedContext?.element) &&
        (isSubtask(relatedForIndex) || (!relatedForIndex && !!e.relatedContext?.component?.$attrs?.taskId));

    if (e.draggedContext?.element) {
        state.dragState.cardBeingDragged = e.draggedContext.element;
        if (!isStoryOverStoryCell) {
            state.dragState.newIndex = e.draggedContext.futureIndex;
        }
    } else {
        // if event is raw Sortable event, there's no draggedContext

        // if the dragged element is an activity
        if (e.from.classList.contains('backbone')) {
            const orderedActivities = orderBy(state.activities, 'order');
            //e.oldIndex contains the index in state.activities which is the dragged element
            state.dragState.cardBeingDragged = orderedActivities[e.oldIndex];
            state.dragState.newIndex = e.oldIndex;
        }
        // need to implement for tasks and subtasks if need to update state from onDragStart
    }

    if (e.from !== null && e.from !== e.to) {
        if (state.dragState.isCardDraggedOut === false) {
            state.dragState.hasJustBeenDraggedOut = true;
            setTimeout(() => {
                state.dragState.hasJustBeenDraggedOut = false;
            }, 100);
        }
        state.dragState.isCardDraggedOut = true;
    } else {
        if (state.dragState.isCardDraggedOut === true) {
            state.dragState.hasJustBeenDraggedOut = true;
            setTimeout(() => {
                state.dragState.hasJustBeenDraggedOut = false;
            }, 100);
        }
        state.dragState.isCardDraggedOut = false;
    }

    // rleatedContext can be null if the target container is empty
    const relatedElement = e.relatedContext?.element;

    // Readers only tell "a task", "an activity" and "nothing" apart, so which task it is does not need to be published.
    if (isStoryOverStoryCell && isTask(state.dragState.targetParentCard)) {
        return;
    }

    if (isActivity(relatedElement)) {
        state.dragState.targetParentCard = null;
    } else if (isTask(relatedElement)) {
        state.dragState.targetParentCard = getCardById(relatedElement.activityId);
    } else if (!relatedElement && e.relatedContext?.component?.$attrs?.activityId) {
        // if the card is dragged into an empty activity, it wont have a related element
        // activityId is set on the draggable component for the tasks
        state.dragState.targetParentCard = getCardById(e.relatedContext.component.$attrs.activityId);
    } else if (isSubtask(relatedElement)) {
        state.dragState.targetParentCard = getCardById(relatedElement.taskId);
    } else if (!relatedElement && e.relatedContext?.component?.$attrs?.taskId) {
        // if the card is dragged into an empty task, it wont have a related element
        // taskId is set on the draggable component for the subtasks
        state.dragState.targetParentCard = getCardById(e.relatedContext.component.$attrs.taskId);
    }
};

//@ts-ignore
registerReducer(SetIsDraggingCard.ACTION_TYPE, reducer);
